import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import * as adminApi from '../api/adminApi';
import Button from '../components/common/Button';

function CompanyDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('metadata');

  // Add user form
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', confirmPassword: '', role: 'user' });
  const [addingUser, setAddingUser] = useState(false);
  const [userError, setUserError] = useState('');

  // Edit user
  const [editingUserId, setEditingUserId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', password: '', confirmPassword: '', role: 'user' });
  const [editError, setEditError] = useState('');
  const [savingUser, setSavingUser] = useState(false);

  // Delete
  const [deletingUserId, setDeletingUserId] = useState(null);

  useEffect(() => {
    fetchCompany();
    fetchUsers();
  }, [id]);

  const fetchCompany = async () => {
    try {
      const response = await adminApi.getCompany(id);
      setCompany(response.data.company);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await adminApi.getCompanyUsers(id);
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setUserError('');

    if (!newUser.name || !newUser.email || !newUser.password) {
      setUserError('All fields are required');
      return;
    }
    if (newUser.password !== newUser.confirmPassword) {
      setUserError('Passwords do not match');
      return;
    }
    if (newUser.password.length < 8) {
      setUserError('Password must be at least 8 characters');
      return;
    }

    try {
      setAddingUser(true);
      await adminApi.addCompanyUser(id, {
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role
      });
      setNewUser({ name: '', email: '', password: '', confirmPassword: '', role: 'user' });
      setShowAddUser(false);
      fetchUsers();
    } catch (err) {
      setUserError(err.message || 'Failed to add user');
    } finally {
      setAddingUser(false);
    }
  };

  const handleStartEdit = (u) => {
    setEditingUserId(u._id);
    setEditForm({ name: u.name, email: u.email, password: '', confirmPassword: '', role: u.role });
    setEditError('');
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditError('');
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setEditError('');

    if (!editForm.name || !editForm.email) {
      setEditError('Name and email are required');
      return;
    }
    if (editForm.password && editForm.password !== editForm.confirmPassword) {
      setEditError('Passwords do not match');
      return;
    }
    if (editForm.password && editForm.password.length < 8) {
      setEditError('Password must be at least 8 characters');
      return;
    }

    const updates = {
      name: editForm.name,
      email: editForm.email,
      role: editForm.role,
    };
    if (editForm.password) {
      updates.password = editForm.password;
      updates.confirmPassword = editForm.confirmPassword;
    }

    try {
      setSavingUser(true);
      await adminApi.updateCompanyUser(id, editingUserId, updates);
      setEditingUserId(null);
      fetchUsers();
    } catch (err) {
      setEditError(err.message || 'Failed to update user');
    } finally {
      setSavingUser(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    try {
      setDeletingUserId(userId);
      await adminApi.deleteCompanyUser(id, userId);
      fetchUsers();
    } catch (err) {
      alert(err.message || 'Failed to delete user');
    } finally {
      setDeletingUserId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (user?.role !== 'super_admin') {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">You do not have access to this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>
      </div>
    );
  }

  const tabs = [
    { key: 'metadata', label: 'Metadata' },
    { key: 'users', label: 'Users' },
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{company?.name}</h1>
        <p className="mt-1 text-sm text-gray-500">Company details and user management</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Metadata Tab */}
      {activeTab === 'metadata' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Company Name</dt>
              <dd className="mt-1 text-sm text-gray-900">{company?.name}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Slug</dt>
              <dd className="mt-1 text-sm text-gray-900">
                <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">{company?.slug}</code>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Created</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatDate(company?.createdAt)}</dd>
            </div>
          </dl>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Users</h2>
            <Button onClick={() => setShowAddUser(!showAddUser)}>
              {showAddUser ? 'Cancel' : '+ Add User'}
            </Button>
          </div>

          {/* Add User Form */}
          {showAddUser && (
            <form onSubmit={handleAddUser} className="p-6 border-b border-gray-200 bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    value={newUser.confirmPassword}
                    onChange={(e) => setNewUser({ ...newUser, confirmPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              {userError && (
                <p className="mt-3 text-sm text-red-600">{userError}</p>
              )}
              <div className="mt-4">
                <Button type="submit" loading={addingUser}>
                  Add User
                </Button>
              </div>
            </form>
          )}

          {/* Users List */}
          {users.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <p>No users in this company.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {users.map((u) =>
                editingUserId === u._id ? (
                  <form key={u._id} onSubmit={handleSaveEdit} className="p-6 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input
                          type="email"
                          value={editForm.email}
                          onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                        <input
                          type="password"
                          value={editForm.password}
                          onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Leave blank to keep current"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                        <input
                          type="password"
                          value={editForm.confirmPassword}
                          onChange={(e) => setEditForm({ ...editForm, confirmPassword: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Leave blank to keep current"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                        <select
                          value={editForm.role}
                          onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                    </div>
                    {editError && (
                      <p className="mt-3 text-sm text-red-600">{editError}</p>
                    )}
                    <div className="mt-4 flex items-center space-x-3">
                      <Button type="submit" loading={savingUser}>Save</Button>
                      <Button variant="secondary" onClick={handleCancelEdit} disabled={savingUser}>Cancel</Button>
                    </div>
                  </form>
                ) : (
                  <div key={u._id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-medium">
                          {u.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{u.name}</p>
                        <p className="text-sm text-gray-500">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        u.role === 'admin'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {u.role}
                      </span>
                      <span className="text-sm text-gray-500">
                        Joined {formatDate(u.createdAt)}
                      </span>
                      <Button variant="secondary" size="sm" onClick={() => handleStartEdit(u)}>
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteUser(u._id)}
                        loading={deletingUserId === u._id}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CompanyDetail;
